/**
 * Equipment Data Index
 * Exports all equipment data: weapons, armor, adventuring gear, tools, packs, and magic items
 */

// Weapons
export {
  weapons,
  getWeapon,
  findWeaponByName,
  getWeaponAbility,
  formatAttackBonus,
  formatDamage,
  isAnyWeaponToken,
  getWeaponsForToken,
  formatWeaponTokenLabel,
} from './weapons';
export type { Weapon, WeaponProperty } from './weapons';

// Armor
export {
  armor,
  getArmor,
  getArmorByCategory,
  calculateArmorAC,
  meetsStrengthRequirement,
  formatArmorAC,
} from './armor';
export type { Armor } from './armor';

// Adventuring Gear
export {
  adventuringGear,
  getAdventuringGear,
  getGearByCategory,
  searchGear,
} from './adventuring-gear';
export type { AdventuringGear, GearCategory } from './adventuring-gear';

// Tools
export {
  tools,
  getTool,
  getToolsByCategory,
  getArtisanTools,
  getGamingSets,
  getMusicalInstruments,
  searchTools,
} from './tools';
export type { Tool, ToolCategory } from './tools';

// Equipment Packs
export {
  equipmentPacks,
  getEquipmentPack,
  getPackContentsList,
} from './packs';
export type { EquipmentPack, PackContents } from './packs';

// Magic Items
export {
  magicItems,
  getMagicItem,
  getMagicItemsByType,
  getMagicItemsByRarity,
  getAttunementItems,
  searchMagicItems,
  formatRarity,
  RARITY_ORDER,
} from './magic-items';
export type { MagicItem, MagicItemRarity, MagicItemType } from './magic-items';

// Lookup maps for efficient access
import { weapons } from './weapons';
import { armor } from './armor';
import { adventuringGear } from './adventuring-gear';
import { tools } from './tools';
import { equipmentPacks } from './packs';
import { magicItems } from './magic-items';
import type { Weapon } from './weapons';
import type { Armor } from './armor';
import type { AdventuringGear } from './adventuring-gear';
import type { Tool } from './tools';
import type { EquipmentPack } from './packs';
import type { MagicItem } from './magic-items';

export const weaponMap = new Map<string, Weapon>(weapons.map((w) => [w.id, w]));
export const armorMap = new Map<string, Armor>(armor.map((a) => [a.id, a]));
export const gearMap = new Map<string, AdventuringGear>(adventuringGear.map((g) => [g.id, g]));
export const toolMap = new Map<string, Tool>(tools.map((t) => [t.id, t]));
export const packMap = new Map<string, EquipmentPack>(equipmentPacks.map((p) => [p.id, p]));
export const magicItemMap = new Map<string, MagicItem>(magicItems.map((m) => [m.id, m]));

/**
 * Combined equipment search across all categories
 */
export function searchEquipment(query: string): {
  weapons: Weapon[];
  armor: Armor[];
  gear: AdventuringGear[];
  tools: Tool[];
  magicItems: MagicItem[];
} {
  const normalized = query.toLowerCase().trim();

  return {
    weapons: weapons.filter(
      (w) =>
        w.name.toLowerCase().includes(normalized) ||
        w.id.includes(normalized)
    ),
    armor: armor.filter(
      (a) =>
        a.name.toLowerCase().includes(normalized) ||
        a.id.includes(normalized)
    ),
    gear: adventuringGear.filter(
      (g) =>
        g.name.toLowerCase().includes(normalized) ||
        g.id.includes(normalized)
    ),
    tools: tools.filter(
      (t) =>
        t.name.toLowerCase().includes(normalized) ||
        t.id.includes(normalized)
    ),
    magicItems: magicItems.filter(
      (m) =>
        m.name.toLowerCase().includes(normalized) ||
        m.id.includes(normalized)
    ),
  };
}

/**
 * Get any equipment item by ID (searches all categories)
 */
export function getEquipmentById(id: string):
  | { type: 'weapon'; item: Weapon }
  | { type: 'armor'; item: Armor }
  | { type: 'gear'; item: AdventuringGear }
  | { type: 'tool'; item: Tool }
  | { type: 'pack'; item: EquipmentPack }
  | { type: 'magic-item'; item: MagicItem }
  | undefined {
  const weapon = weaponMap.get(id);
  if (weapon) return { type: 'weapon', item: weapon };

  const armorItem = armorMap.get(id);
  if (armorItem) return { type: 'armor', item: armorItem };

  const gear = gearMap.get(id);
  if (gear) return { type: 'gear', item: gear };

  const tool = toolMap.get(id);
  if (tool) return { type: 'tool', item: tool };

  const pack = packMap.get(id);
  if (pack) return { type: 'pack', item: pack };

  const magicItem = magicItemMap.get(id);
  if (magicItem) return { type: 'magic-item', item: magicItem };

  return undefined;
}
